import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, CheckCircle, Building, Clock, ArrowRight, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const groupPricing = [
  { course: 'Heartsaver CPR/AED', price: '$70' },
  { course: 'Heartsaver First Aid', price: '$70' },
  { course: 'CPR/AED & First Aid', price: '$100' },
  { course: 'BLS for Healthcare Providers', price: '$80' },
  { course: 'BLS AND First Aid', price: '$110' },
  { course: 'Blood Borne Pathogens', price: '$40' },
];

const skillsTestPricing = [
  { service: 'Skills Test Only – CPR/First Aid', price: '$60' },
  { service: 'Skills Test Only – BLS', price: '$55' },
];

const benefits = [
  'Minimum group size: 6 participants',
  'On-site training at your location',
  '2-3 hour class duration',
  'Bulk discounts available',
  'Flexible scheduling',
  'Team certification tracking',
];

export default function GroupClasses() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#EDF6F9] via-white to-[#83C5BE]/20 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E29578]/20 text-[#E29578] text-sm font-medium mb-6">
              <Users className="w-4 h-4" />
              <span>Min 6 Participants</span>
            </div>
            <h1 
              className="text-4xl md:text-5xl font-bold text-[#264653] mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="group-classes-title"
            >
              Group Classes & Skills Testing
            </h1>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Perfect for businesses, churches, schools, and organizations. 
              We bring professional training to your team at your location.
            </p>
            <Link to="/book-now">
              <Button 
                size="lg" 
                className="rounded-full bg-primary hover:bg-primary/90 px-8 py-6 text-lg"
                data-testid="request-group-quote-btn"
              >
                Request Group Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#264653] mb-6"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Why Group Training?
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Group training is the most efficient and cost-effective way to certify your team. 
                We come to you, so there's no travel time or expense for your staff.
              </p>
              <ul className="space-y-4">
                {benefits.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-[#264653]">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              <Card className="p-6 bg-[#EDF6F9] border-0" data-testid="group-benefit-1">
                <CardContent className="p-0">
                  <Building className="w-8 h-8 text-primary mb-3" />
                  <p className="font-medium text-[#264653]">On-Site Training</p>
                  <p className="text-sm text-muted-foreground mt-1">We come to your location</p>
                </CardContent>
              </Card>
              <Card className="p-6 bg-[#EDF6F9] border-0" data-testid="group-benefit-2">
                <CardContent className="p-0">
                  <Clock className="w-8 h-8 text-[#83C5BE] mb-3" />
                  <p className="font-medium text-[#264653]">2-3 Hours</p>
                  <p className="text-sm text-muted-foreground mt-1">Efficient class duration</p>
                </CardContent>
              </Card>
              <Card className="p-6 bg-[#EDF6F9] border-0" data-testid="group-benefit-3">
                <CardContent className="p-0">
                  <Users className="w-8 h-8 text-[#E29578] mb-3" />
                  <p className="font-medium text-[#264653]">Team Building</p>
                  <p className="text-sm text-muted-foreground mt-1">Learn together, save together</p>
                </CardContent>
              </Card>
              <Card className="p-6 bg-[#EDF6F9] border-0" data-testid="group-benefit-4">
                <CardContent className="p-0">
                  <MapPin className="w-8 h-8 text-primary mb-3" />
                  <p className="font-medium text-[#264653]">Georgia-Wide</p>
                  <p className="text-sm text-muted-foreground mt-1">Serving all of Georgia</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Group Pricing */}
      <section className="py-16 md:py-24 bg-[#EDF6F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#264653] mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Group Class Pricing
            </h2>
            <p className="text-muted-foreground">Per person pricing (minimum 6 participants)</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="max-w-2xl mx-auto border-slate-100 overflow-hidden" data-testid="group-pricing-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary/5">
                    <TableHead className="text-[#264653] font-semibold text-base">Course</TableHead>
                    <TableHead className="text-right text-[#264653] font-semibold text-base">Per Person</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupPricing.map((item, index) => (
                    <TableRow key={index} className="hover:bg-primary/5">
                      <TableCell className="font-medium text-[#264653]">{item.course}</TableCell>
                      <TableCell className="text-right text-primary font-bold text-lg">{item.price}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Skills Testing */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#264653] mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Skills Testing Only
            </h2>
            <p className="text-muted-foreground">For those who completed online training elsewhere</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="max-w-xl mx-auto border-slate-100 overflow-hidden" data-testid="skills-test-pricing-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#E29578]/10">
                    <TableHead className="text-[#264653] font-semibold text-base">Service</TableHead>
                    <TableHead className="text-right text-[#264653] font-semibold text-base">Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {skillsTestPricing.map((item, index) => (
                    <TableRow key={index} className="hover:bg-[#E29578]/5">
                      <TableCell className="font-medium text-[#264653]">{item.service}</TableCell>
                      <TableCell className="text-right text-[#E29578] font-bold text-lg">{item.price}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </motion.div>
          <div className="text-center mt-10">
            <Link to="/book-now">
              <Button 
                size="lg" 
                className="rounded-full bg-primary hover:bg-primary/90 px-8 py-6 text-lg"
                data-testid="book-group-btn"
              >
                Request Group Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
